<template>
    <div class="header-wrapper" :class="{ 'scrolled': isScrolled }">
        <!-- Liquid Glass Header -->
        <header class="liquid-glass-header">
            <div class="liquid-glass-header__bend" aria-hidden="true"></div>
            <div class="liquid-glass-header__face" aria-hidden="true"></div>
            <div class="liquid-glass-header__shine" aria-hidden="true"></div>
            <div class="liquid-glass-header__edge" aria-hidden="true"></div>
            
            <!-- Header Content -->
            <div class="liquid-glass-header__content">
                <!-- Main header -->
                <div class="py-1.5 lg:py-2">
                    <div class="header-desktop hidden nav:flex container w-full items-center gap-3 xl:gap-5">
                        <HeaderLogo class="shrink-0" />

                        <HeaderSearch class="min-w-0 flex-1" />

                        <div class="flex shrink-0 items-center gap-4 xl:gap-6">
                            <router-link
                                to="/catalog/"
                                class="btn btn-outline btn-catalog header-catalog-btn"
                            >
                                <PhSquaresFour :size="16" :weight="'regular'" class="btn-catalog-icon shrink-0 transition-colors" />
                                <span>Каталог</span>
                            </router-link>
                            <HeaderRegions />
                            <HeaderUser />
                            <HeaderCart />
                        </div>
                    </div>

                    <div class="flex nav:hidden items-center container min-h-11">
                        <!-- Логотип слева -->
                        <HeaderLogo :class="{ 'opacity-0 pointer-events-none': isMenuOpen }" />
                        
                        <!-- Spacer для растягивания -->
                        <div class="flex-1"></div>
                        
                        <!-- Иконки и бургер справа -->
                        <div class="header-mobile-actions flex items-center gap-1">
                            <HeaderUser :class="{ 'opacity-0 pointer-events-none': isMenuOpen }" />
                            <HeaderCart :class="{ 'opacity-0 pointer-events-none': isMenuOpen }" />
                            <!-- Burger button - самый правый край -->
                            <button 
                                @click="toggleMenu" 
                                class="mobile-burger-icon flex-shrink-0 transition-colors hover:!text-brand" 
                                :class="{ '!text-brand': isMenuOpen }"
                                :aria-label="isMenuOpen ? 'Закрыть меню' : 'Открыть меню'"
                                :aria-expanded="isMenuOpen"
                                aria-controls="mobile-menu"
                            >
                                <PhX v-if="isMenuOpen" :size="22" :weight="'regular'" :color="iconColor" />
                                <svg v-else :width="22" :height="22" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="7" width="20" height="2" rx="1" :fill="iconColor"/>
                                    <rect x="3" y="17" width="20" height="2" rx="1" :fill="iconColor"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </header>
        
        <!-- Mobile menu - вынесен за пределы хедера для правильного z-index -->
        <MobileMenu
            :is-open="isMenuOpen"
            :regions="regions"
            :selected-region="selectedRegion"
            @close="closeMenu"
            @region-change="handleRegionChange"
        />
    </div>
</template>

<script>
import HeaderLogo from '@/components/header/HeaderLogo.vue'
import HeaderRegions from '@/components/header/HeaderRegions.vue'
import HeaderSearch from '@/components/header/HeaderSearch.vue'
import HeaderUser from '@/components/header/HeaderUser.vue'
import MobileMenu from '@/components/header/MobileMenu.vue'
import HeaderCart from '@/components/header/HeaderCart.vue'
import { PhX, PhSquaresFour } from '@phosphor-icons/vue'
import gql from 'graphql-tag'
import { useAuth } from '@/store/useAuth'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import { useThrottle } from '@/composables/useThrottle'
import { logError } from '@/utils/logger'

// Константы
const SCROLL_THRESHOLD = 10 // px

export default {
    components: {
        HeaderLogo, 
        HeaderRegions, 
        HeaderSearch, 
        HeaderUser, 
        HeaderCart, 
        MobileMenu,
        PhX,
        PhSquaresFour
    },
    setup() {
        const authInfo = useAuth()
        const { lock, unlock } = useBodyScrollLock()
        
        return { 
            authInfo,
            lock,
            unlock
        }
    },
    apollo: {
        regions: {
            query: gql`query {
                regions {
                    id
                    name
                }
            }`,
            // Используем cache-first для статичных данных (регионы редко меняются)
            fetchPolicy: 'cache-first',
            error(error) {
                logError('Failed to load regions', error)
                // Можно добавить уведомление пользователю
            }
        }
    },
    data() {
        return {
            isMenuOpen: false,
            isScrolled: false,
            regions: [],
            selectedRegion: null
        }
    },
    computed: {
        iconColor() {
            // Используем CSS переменные вместо hex-кодов
            const root = getComputedStyle(document.documentElement)
            return this.isMenuOpen 
                ? root.getPropertyValue('--icon-primary').trim() || '#DC2626'
                : root.getPropertyValue('--icon-default').trim() || '#475569'
        }
    },
    created() {
        this.authInfo.getViewer();
        this.selectedRegion = this.authInfo.region.id;
        // Throttle scroll handler для оптимизации производительности
        this.throttledScroll = useThrottle(this.handleScroll, 100);
    },
    mounted() {
        window.addEventListener('scroll', this.throttledScroll, { passive: true });
        document.addEventListener('keydown', this.handleKeydown);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.throttledScroll);
        document.removeEventListener('keydown', this.handleKeydown);
        // Очищаем throttle функцию
        if (this.throttledScroll && this.throttledScroll.cancel) {
            this.throttledScroll.cancel();
        }
        this.unlock();
    },
    watch: {
        $route() {
            // Закрываем меню при переходе на другую страницу
            this.closeMenu();
        }
    },
    methods: {
        toggleMenu() {
            this.isMenuOpen = !this.isMenuOpen;
            this.updateBodyScroll(this.isMenuOpen);
        },
        closeMenu() {
            this.isMenuOpen = false;
            this.updateBodyScroll(false);
        },
        updateBodyScroll(lock) {
            if (lock) {
                this.lock();
            } else {
                this.unlock();
            }
        },
        handleRegionChange(regionId) {
            this.selectedRegion = regionId;
            this.authInfo.logInGuest(regionId);
        },
        handleScroll() {
            const scrolled = window.scrollY > SCROLL_THRESHOLD;
            if (scrolled !== this.isScrolled) {
                this.isScrolled = scrolled;
            }
        },
        handleKeydown(event) {
            // Закрываем меню по Escape
            if (event.key === 'Escape' && this.isMenuOpen) {
                this.closeMenu();
            }
        }
    }
}
</script>

<style scoped>
/* ===== LIQUID GLASS HEADER ===== */
.header-wrapper {
  position: -webkit-sticky;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  isolation: isolate;
  --mobile-header-height: 3.75rem;
}

.liquid-glass-header {
  position: relative;
  width: 100%;
  overflow: visible;
  border: none;
  outline: none;
  background: rgba(255, 255, 255, 0.18);
  border-bottom: 1px solid rgba(255, 255, 255, 0.42);
  box-shadow:
    0 4px 24px rgba(15, 23, 42, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.55);
  transition:
    background-color 0.35s ease,
    box-shadow 0.35s ease,
    border-color 0.35s ease;
}

.liquid-glass-header__bend {
  position: absolute;
  inset: 0;
  z-index: 0;
  backdrop-filter: blur(22px) saturate(1.65) brightness(1.04);
  -webkit-backdrop-filter: blur(22px) saturate(1.65) brightness(1.04);
}

.liquid-glass-header__face {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.52) 0%,
    rgba(255, 255, 255, 0.14) 42%,
    rgba(255, 255, 255, 0.06) 100%
  );
}

.liquid-glass-header__shine {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  opacity: 0.65;
  background:
    radial-gradient(
      120% 80% at 12% -20%,
      rgba(255, 255, 255, 0.75) 0%,
      rgba(255, 255, 255, 0.18) 34%,
      transparent 62%
    ),
    linear-gradient(
      115deg,
      rgba(255, 255, 255, 0.22) 0%,
      transparent 38%,
      rgba(255, 255, 255, 0.08) 100%
    );
  mix-blend-mode: soft-light;
}

.liquid-glass-header__edge {
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  box-shadow:
    inset 0 -1px 0 rgba(15, 23, 42, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.liquid-glass-header__content {
  position: relative;
  z-index: var(--z-header-content, 4);
}

.header-wrapper.scrolled .liquid-glass-header {
  background: rgba(255, 255, 255, 0.34);
  border-bottom-color: rgba(255, 255, 255, 0.5);
  box-shadow:
    0 10px 32px rgba(15, 23, 42, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.header-wrapper.scrolled .liquid-glass-header__bend {
  backdrop-filter: blur(28px) saturate(1.75) brightness(1.03);
  -webkit-backdrop-filter: blur(28px) saturate(1.75) brightness(1.03);
}

.header-wrapper.scrolled .liquid-glass-header__face {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.62) 0%,
    rgba(255, 255, 255, 0.22) 48%,
    rgba(255, 255, 255, 0.1) 100%
  );
}

.header-wrapper.scrolled .liquid-glass-header__shine {
  opacity: 0.8;
}

@media (min-width: 992px) {
  .liquid-glass-header__bend {
    backdrop-filter: blur(26px) saturate(1.7) brightness(1.04);
    -webkit-backdrop-filter: blur(26px) saturate(1.7) brightness(1.04);
  }

  .header-wrapper.scrolled .liquid-glass-header__bend {
    backdrop-filter: blur(32px) saturate(1.8) brightness(1.03);
    -webkit-backdrop-filter: blur(32px) saturate(1.8) brightness(1.03);
  }
}

@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
  .liquid-glass-header {
    background: rgba(255, 255, 255, 0.94);
  }

  .header-wrapper.scrolled .liquid-glass-header {
    background: rgba(255, 255, 255, 0.98);
  }
}

.header-desktop {
  min-height: 3rem;
}

.mobile-burger-icon {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 100000;
  width: 22px;
  height: 22px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-icon {
  width: 22px;
  height: 22px;
}

@media (min-width: 992px) {
  .mobile-burger-icon {
    display: none;
  }
}

@media (max-width: 991px) {
  .header-mobile-actions :deep(.header-user),
  .header-mobile-actions :deep(.header-cart-link) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
  }
}
</style>
